import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Button from './Button';
import Icon from '../AppIcon';

const AdminPanel = () => {
  const { user, profile, isAdmin } = useAuth();
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeSubscriptions: 0,
    totalLeads: 0,
    systemHealth: 'good'
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (isAdmin) {
      fetchAdminStats();
      fetchRecentActivity();
    }
  }, [isAdmin]);

  const fetchAdminStats = async () => {
    try {
      // Fetch user count
      const { count: userCount } = await supabase
        ?.from('user_profiles')
        ?.select('*', { count: 'exact', head: true });

      // Fetch active subscriptions
      const { count: subCount } = await supabase
        ?.from('subscriptions')
        ?.select('*', { count: 'exact', head: true })
        ?.eq('status', 'active');

      // Fetch leads count (using funnel_leads table)
      const { count: leadsCount } = await supabase
        ?.from('funnel_leads')
        ?.select('*', { count: 'exact', head: true });

      setStats({
        totalUsers: userCount || 0,
        activeSubscriptions: subCount || 0,
        totalLeads: leadsCount || 0,
        systemHealth: 'good'
      });
    } catch (error) {
      console.error('Error fetching admin stats:', error);
    }
  };

  const fetchRecentActivity = async () => {
    try {
      // Get recent user registrations
      const { data: recentUsers } = await supabase
        ?.from('user_profiles')
        ?.select('id, email, created_at')
        ?.order('created_at', { ascending: false })
        ?.limit(5);

      const activities = recentUsers?.map(user => ({
        id: user?.id,
        type: 'user_registered',
        description: `New user registered: ${user?.email}`,
        timestamp: user?.created_at
      })) || [];

      setRecentActivity(activities);
    } catch (error) {
      console.error('Error fetching recent activity:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSystemAction = async (action) => {
    try {
      switch (action) {
        case 'refresh_stats':
          setIsLoading(true);
          await fetchAdminStats();
          await fetchRecentActivity();
          setIsLoading(false);
          break;
        case 'system_health':
          // Perform system health check
          console.log('System health check initiated');
          break;
        default:
          console.log(`Admin action: ${action}`);
      }
    } catch (error) {
      console.error('Error performing admin action:', error);
    }
  };

  // Don't render if not admin
  if (!isAdmin) {
    return null;
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-elevation-1">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <Icon name="Shield" size={24} className="text-red-600 mr-2" />
          <h2 className="text-lg font-semibold text-foreground">Admin Panel</h2>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
          <span className="text-sm text-muted-foreground">System Online</span>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : (
        <>
          {/* Admin Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-background rounded-lg p-4 border border-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Users</p>
                  <p className="text-2xl font-bold text-foreground">{stats?.totalUsers}</p>
                </div>
                <Icon name="Users" size={20} className="text-blue-600" />
              </div>
            </div>

            <div className="bg-background rounded-lg p-4 border border-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active Subs</p>
                  <p className="text-2xl font-bold text-foreground">{stats?.activeSubscriptions}</p>
                </div>
                <Icon name="CreditCard" size={20} className="text-green-600" />
              </div>
            </div>

            <div className="bg-background rounded-lg p-4 border border-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Leads</p>
                  <p className="text-2xl font-bold text-foreground">{stats?.totalLeads}</p>
                </div>
                <Icon name="Target" size={20} className="text-purple-600" />
              </div>
            </div>

            <div className="bg-background rounded-lg p-4 border border-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">System Health</p>
                  <p className="text-lg font-semibold text-green-600">Good</p>
                </div>
                <Icon name="Activity" size={20} className="text-green-600" />
              </div>
            </div>
          </div>

          {/* Admin Actions */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-foreground mb-3">Quick Actions</h3>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleSystemAction('refresh_stats')}
                iconName="RefreshCw"
                iconPosition="left"
              >
                Refresh Stats
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleSystemAction('system_health')}
                iconName="Activity"
                iconPosition="left"
              >
                Health Check
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => window?.open('/admin/logs', '_blank')}
                iconName="FileText"
                iconPosition="left"
              >
                View Logs
              </Button>
            </div>
          </div>

          {/* Recent Activity */}
          <div>
            <h3 className="text-sm font-medium text-foreground mb-3">Recent Activity</h3>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {recentActivity?.length > 0 ? (
                recentActivity?.map((activity, index) => (
                  <div key={activity?.id || index} className="flex items-center justify-between py-2 px-3 bg-background rounded border border-border">
                    <div className="flex items-center">
                      <Icon name="User" size={16} className="text-muted-foreground mr-2" />
                      <span className="text-sm text-foreground">{activity?.description}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {activity?.timestamp ? new Date(activity?.timestamp)?.toLocaleDateString() : 'Recent'}
                    </span>
                  </div>
                ))
              ) : (
                <div className="text-center py-4 text-muted-foreground">
                  <Icon name="Clock" size={20} className="mx-auto mb-2" />
                  <p className="text-sm">No recent activity</p>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminPanel;